import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';
import { Link } from 'react-router-dom';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-foreground-950/75 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
              <i className="ri-store-line text-background-50 text-lg"></i>
            </div>
            <span className={`font-heading text-xl font-bold hidden sm:block ${isScrolled ? 'text-foreground-950' : 'text-background-50'}`}>
              Superbuy Spreadsheet
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`font-label text-sm hover:text-primary-400 transition-colors duration-200 ${isScrolled ? 'text-foreground-700' : 'text-background-50/90'}`}
            >
              Home
            </Link>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border font-label text-sm font-medium transition-all duration-200 cursor-pointer ${
                  isScrolled
                    ? 'border-foreground-300/40 text-foreground-800 hover:border-primary-400 hover:text-primary-600'
                    : 'border-background-50/25 text-background-50/90 hover:border-background-50/50 hover:text-background-50'
                }`}
              >
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 max-h-[70vh] overflow-y-auto z-50">
                  <div className="px-4 py-2 border-b border-background-100 mb-1">
                    <p className="font-label text-xs uppercase tracking-wider text-foreground-400">14 Categories</p>
                  </div>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer group"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-100 transition-colors duration-200">
                        <i className={`${cat.icon} text-sm text-primary-500`}></i>
                      </div>
                      <span className="font-label text-sm whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/article/ultimate-superbuy-spreadsheet-guide-2026"
              className={`font-label text-sm hover:text-primary-400 transition-colors duration-200 ${isScrolled ? 'text-foreground-700' : 'text-background-50/90'}`}
            >
              Shopping Guide
            </Link>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 bg-primary-500 text-background-50 font-label text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile hamburger */}
          <div className="flex md:hidden items-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 bg-primary-500 text-background-50 font-label text-xs font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`w-10 h-10 flex items-center justify-center cursor-pointer ${isScrolled ? 'text-foreground-800' : 'text-background-50'}`}
            >
              <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[85vh] overflow-y-auto">
          <div className="px-6 py-4 space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 py-3 text-foreground-800 font-label text-sm font-medium hover:text-primary-600"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="ri-home-line text-base text-primary-500"></i>
              <span>Home</span>
            </Link>

            <Link
              to="/article/ultimate-superbuy-spreadsheet-guide-2026"
              className="flex items-center gap-3 py-3 text-foreground-800 font-label text-sm font-medium hover:text-primary-600"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="ri-book-open-line text-base text-primary-500"></i>
              <span>Shopping Guide</span>
            </Link>

            {/* Mobile Categories Accordion */}
            <div className="border-t border-background-100 pt-2 mt-2">
              <button
                onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
                className="w-full flex items-center justify-between py-3 text-foreground-800 font-label text-sm font-medium cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <i className="ri-apps-line text-base text-primary-500"></i>
                  <span>Categories (14)</span>
                </div>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileCategoryOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileCategoryOpen && (
                <div className="pb-2 pl-9 space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 py-2.5 text-foreground-600 font-label text-sm hover:text-primary-600 cursor-pointer"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <i className={`${cat.icon} text-xs text-primary-500`}></i>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}